/**
 * Plan management use cases (application layer).
 * Validates domain rules before delegating to the repository.
 */
import {
  isValidPlanInput,
  type Plan,
  type PlanCreateInput,
  type PlanUpdateInput,
} from '@domain/plan/Plan';
import type { PlanRepository } from '@domain/plan/PlanRepository';

export class InvalidPlanInput extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'InvalidPlanInput';
  }
}

export const createPlan = (repo: PlanRepository) =>
  async (input: PlanCreateInput): Promise<Plan> => {
    if (!isValidPlanInput(input)) {
      throw new InvalidPlanInput('plan input failed domain validation');
    }
    return repo.create(input);
  };

export const updatePlan = (repo: PlanRepository) =>
  async (id: string, input: PlanUpdateInput): Promise<Plan> => {
    if (input.base_rate !== undefined && Number(input.base_rate) < 0) {
      throw new InvalidPlanInput('base_rate must be ≥ 0');
    }
    if (input.monthly_fee !== undefined && Number(input.monthly_fee) < 0) {
      throw new InvalidPlanInput('monthly_fee must be ≥ 0');
    }
    return repo.update(id, input);
  };

export const togglePlanActive = (repo: PlanRepository) =>
  async (plan: Plan): Promise<Plan> =>
    plan.is_active ? repo.deactivate(plan.id) : repo.reactivate(plan.id);
